import {
  getPathSeparator,
  isPathDescendantOf,
} from 'portfolio-shared/util/pathUtils';
import { PLATFORM_WINDOWS } from 'portfolio-shared/util/sharedConstants';

export const SYNC_STATUS_FILES_TO_ADD = 1;
export const SYNC_STATUS_INCOMPLETELY_SCANNED = 2;
export const SYNC_STATUS_UNAVAILABLE = 4;
export const SYNC_STATUS_ERROR = 8;
export const SYNC_STATUS_CONTAINS_EXCLUDED_FILES = 16;
export const SYNC_STATUS_FILES_MISSING_ON_DISK = 32;
export const SYNC_STATUS_FILES_MODIFIED = 64;

export const FILETYPE = {
  directory: 'directory',
  file: 'file',
};

//folderUtils.js
export function isFolderAccessible(folder) {
  const flags = folder.folderOnlyFlags || 0;
  return (flags & SYNC_STATUS_UNAVAILABLE) === 0;
}

export function isFolderOutOfSync(folder) {
  const flags = folder.flags || 0;
  return (
    0 !==
    (flags &
      (SYNC_STATUS_FILES_TO_ADD +
        SYNC_STATUS_FILES_MISSING_ON_DISK +
        SYNC_STATUS_FILES_MODIFIED))
  );
}

export function isRootFolder(folder) {
  return folder ? folder.isRoot : false;
}

// If recurse is false, return the first visibleFolder that is an ancestor of the path or null.
// If recurse is true, return the possibly nested visibleFolder that is an exact
// match of the path or null.
export function visibleFolderForPath(path, visibleFolders, recurse) {
  let result = null;
  while (visibleFolders && !result) {
    let subfolders = null;
    for (const folder of visibleFolders) {
      if (isPathDescendantOf(path, folder.fullPath)) {
        if (recurse) {
          if (path === folder.fullPath) {
            result = folder;
          } else {
            subfolders = folder.s;
          }
        } else {
          result = folder;
        }
        break;
      }
    }
    visibleFolders = subfolders;
  }
  return result;
}

export function watchFolderForPath(path, watchFolders) {
  for (const id of Object.keys(watchFolders)) {
    const folder = watchFolders[id];
    if (isPathDescendantOf(path, folder.path)) {
      return folder;
    }
  }
  return null;
}

export function watchFolderIdForPath(path, watchFolders) {
  for (const id of Object.keys(watchFolders)) {
    const folder = watchFolders[id];
    if (isPathDescendantOf(path, folder.path)) {
      return id;
    }
  }
  return null;
}

export function getParentFolderPath(path) {
  return (
    path
      .split(':')
      .slice(0, -2)
      .join(':') + ':'
  );
}

// The following functions are used browsing the server filesystem.
// We should be able to adapt them without too much trouble for working with
// watchfolders as well.

/**
 * Given an array of possibly deeply nested folder objects and a currently selected
 * path, get the next or previous path.
 *
 * folders - an array of folder objects
 * cur - the currently selected path
 * isUp - if true, get the previous path, otherwise the next.
 *
 * The simple way to do this would be to iterate through all the expanded
 * folders. I did it a harder way that I hope will scale up better.
 *
 * TODO - pull this out into a utility. This can probably be reused for the
 *   watchfolder list in the organizer panel.
 */
export function getNextPath(folders, cur, isUp) {
  if (!cur) {
    return folders[0].fullPath;
  } else {
    const ancestors = [];
    if (!getFolderAncestorIndicesForPath(folders, cur, ancestors)) {
      return null;
    }

    let tree = null;
    if (isUp) {
      const level = ancestors.length - 1;
      tree = getTreeForAncestorIndices(folders, ancestors.slice(0, level));
      const subfolders = level === 0 ? folders : tree.s;
      if (ancestors[level] > 0) {
        tree = subfolders[ancestors[level] - 1];
        while (tree.isExpanded && tree.s && tree.s.length > 0) {
          tree = tree.s[tree.s.length - 1];
        }
      }
    } else {
      tree = getTreeForAncestorIndices(folders, ancestors);
      if (tree.isExpanded && tree.s && tree.s.length > 0) {
        tree = tree.s[0];
      } else {
        let level = ancestors.length;
        while (--level >= 0) {
          tree = getTreeForAncestorIndices(folders, ancestors.slice(0, level));
          const subfolders = level === 0 ? folders : tree.s;
          if (ancestors[level] < subfolders.length - 1) {
            tree = subfolders[ancestors[level] + 1];
            break;
          }
        }
      }
    }
    return tree ? tree.fullPath : null;
  }
}

function separatorForPath(path) {
  let result = '\\';
  if (path) {
    const c1 = path.charAt(0);
    if (c1 === '/' || c1 === ':') {
      result = c1;
    }
  }
  return result;
}

function appendSeparator(path, sep) {
  let result = path;
  const separator = sep ? sep : separatorForPath(path);
  if (path.lastIndexOf(separator) !== path.length - 1) {
    result = path + separator;
  }
  return result;
}

/**
 * The first argument is an array of tree objects (see below).
 *
 * The ancestors parameter will be populated with
 * all ancestor tree node indexes starting with the given path.
 *
 * Returns tree if-and-only-if the path is found.
 **/
function getFolderAncestorIndicesForPath(
  treeArray,
  path,
  ancestors,
  separator
) {
  let result = false;
  for (let i = 0; i < treeArray.length && !result; ++i) {
    const tree = treeArray[i];
    if (tree.fullPath === path) {
      ancestors.push(i);
      result = true;
    } else if (path.indexOf(appendSeparator(tree.fullPath, separator)) === 0) {
      ancestors.push(i);
      result = getFolderAncestorIndicesForPath(
        tree.s,
        path,
        ancestors,
        separator
      );
    }
  }
  return result;
}

function getTreeForAncestorIndices(treeArray, ancestors) {
  let result = null;
  for (let i = 0; i < ancestors.length; ++i) {
    if (!result) {
      result = treeArray[ancestors[i]];
    } else {
      result = result.s[ancestors[i]];
    }
  }
  return result;
}

// The first argument is either a tree object, or an array of tree objects.
// The tree objects look like:
// { fullPath: '/Absolute/path/name', displayName: 'name', isExpanded: false, s: [{ <folder object> }, ...] }
export function getFolderForPath(tree, path, separator) {
  if (!Array.isArray(tree)) {
    tree = [tree];
  }
  const ancestors = [];
  if (getFolderAncestorIndicesForPath(tree, path, ancestors, separator)) {
    return getTreeForAncestorIndices(tree, ancestors);
  }
  return null;
}

// This is called from the selector getFolderListRowCount
export function folderListRowCount(folders) {
  return folders.reduce(
    (total, folder) =>
      total + 1 + (folder.isExpanded && folderListRowCount(folder.s)),
    0
  );
}

export function getFolderAndLevelForRow(folders, row) {
  let currentRow = 0;
  let watchFolderId = 0;

  function getFolderForRowInner(folders, row, level) {
    for (let i = 0; i < folders.length; i++) {
      if (level === 0) {
        watchFolderId = folders[i].watchFolderId;
      }

      if (row === currentRow) {
        return { folder: folders[i], level, watchFolderId };
      } else if (folders[i].isExpanded) {
        currentRow++;
        const folder = getFolderForRowInner(folders[i].s, row, level + 1);
        if (folder) {
          return folder;
        } else {
          continue;
        }
      }

      currentRow++;
    }
  }

  return getFolderForRowInner(folders, row, 0);
}

// The rawResult is an array of objects that have a name
// and a type. The name is an absolute path for root directories,
// and just a name for all subfolders.
//
// We need to merge these into the serverFolders that contain objects
// with a fullPath member which is an absolute path, a displayName,
// a flag isExpanded, and an array of subfolders called s.
export function mergeServerFoldersResult(rawResult, path, state) {
  if (!path) {
    path = '';
  }
  const separator = getPathSeparator(
    PLATFORM_WINDOWS === state.serverOsPlatform
  );

  // If the path is empty, the name member will contain an absolute path.
  // Otherwise, it will contain only a subfolder name, so we compute the prefix
  // that we need to make the fullPath.
  let prefix = path;
  if (prefix.length > 0 && prefix.substring(path.length - 1) !== separator) {
    prefix += separator;
  }
  const folderArray = [];
  // stringify and parse in order to make a deep copy so we don't modify the state.
  const result = path
    ? JSON.parse(JSON.stringify(state.serverFolders))
    : folderArray;
  for (let i = 0; i < rawResult.length; ++i) {
    const name = rawResult[i].name;
    const pos = name.slice(0, name.length - 1).lastIndexOf(separator);
    const folder = {
      fullPath: prefix + name,
      displayName: name.substring(pos + 1),
      type: rawResult[i].type,
    };
    folderArray.push(folder);
  }
  // Now we have the new folder array, so sort it.
  folderArray.sort((a, b) => {
    return a.displayName.localeCompare(b.displayName);
  });
  if (path) {
    // If path is non-empty, find the folder in which to insert the array of subfolders.
    const folder = getFolderForPath(result, path, separator);
    if (folder) {
      // TODO - merge the folder array if there as an existing array of subfolders.
      folder.s = folderArray;
    }
  }
  return result;
}



// WEBPACK FOOTER //
// ./src/portfolio-shared/util/folderUtils.js